<template>
    <div class="policies">
        <ul v-if="Object.keys(row.read(record)).length > 0" class="policies__list">
            <li
                v-for="(policy, policyName) in row.read(record)"
                :key="policyName"
                class="policies__row"
                :class="{ 'policies__row--reading': !editing }"
            >
                <code>{{ policyName }}</code>
                <!-- The body, which the Edit button is the only other way to
                     read. A name on its own is not a view of a policy. -->
                <code v-if="!editing" class="policies__body">{{ bodyOf(policy) }}</code>
                <Button
                    v-if="editing"
                    label="Edit"
                    severity="secondary"
                    variant="text"
                    size="small"
                    @click="edit(policyName, policy)"
                />
                <Button
                    v-if="editing"
                    icon="pi pi-times"
                    severity="secondary"
                    variant="text"
                    rounded
                    size="small"
                    :disabled="busy === policyName"
                    :aria-label="`Clear ${policyName}`"
                    @click="clear(policyName)"
                />
            </li>
        </ul>
        <p v-else class="policies__empty">
            None. <template v-if="!editing">Edit to add one.</template>
        </p>

        <div v-if="editing" class="policies__form">
            <InputText v-model="name" placeholder="Policy name — buyerFees" aria-label="Policy name" />
            <Textarea v-model="body" rows="3" auto-resize aria-label="Policy body" />
            <Button
                label="Set policy"
                icon="pi pi-check"
                size="small"
                :loading="busy !== ''"
                @click="save"
            />
        </div>

        <p v-if="invalid" class="policies__failure">{{ invalid }}</p>
        <p v-else-if="failure" class="policies__failure">
            {{ failureText(failure) }}
        </p>
    </div>
</template>

<script setup>
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import { ref } from 'vue'

import { failureText } from '@/lib/failureText.js'

// Named settings, each set and cleared by its own name.
//
// JSON rather than a form, because the shape of a policy is the policy's own —
// the server stores what it is given and says the application validates it
// against the key, and no application defines one yet. A form would be inventing
// fields nobody has agreed on.
const props = defineProps({
    row: { type: Object, required: true },
    record: { type: Object, required: true },
    store: { type: Object, required: true },
    // False names each policy and shows its body, and offers nothing that sets
    // or clears one. Both of those write the moment they are pressed.
    editing: { type: Boolean, default: false }
})
const emit = defineEmits(['changed'])

/** Compact, because this sits in a rail 420 pixels wide. */
function bodyOf(policy) {
    return JSON.stringify(policy ?? {})
}

const name = ref('')
const body = ref('{}')
const busy = ref('')
const failure = ref(null)
const invalid = ref('')

function edit(policyName, policy) {
    name.value = policyName
    body.value = JSON.stringify(policy, null, 2)
    invalid.value = ''
}

// `changed` only on success. The parent bumps a version key on that event and
// the key remounts this component, which destroys the `failure` ref that had
// just caught the refusal — so reporting a refusal and announcing it in the
// same breath meant never reporting one at all.
async function run(key, work) {
    busy.value = key
    failure.value = null
    const ok = await work()
    busy.value = ''
    if (ok === false) {
        failure.value = props.store.error
        return false
    }
    emit('changed')
    return true
}

async function save() {
    if (name.value.trim() === '') {
        invalid.value = 'A policy needs a name'
        return
    }
    let parsed
    try {
        parsed = JSON.parse(body.value === '' ? '{}' : body.value)
    } catch {
        invalid.value = 'That is not valid JSON'
        return
    }
    invalid.value = ''
    const policyName = name.value.trim()
    const ok = await run(policyName, () => props.row.set(props.store, props.record._id, policyName, parsed))
    if (ok === false) {
        return
    }
    name.value = ''
    body.value = '{}'
}

function clear(policyName) {
    return run(policyName, () => props.row.clear(props.store, props.record._id, policyName))
}
</script>

<style scoped>
.policies__list {
    list-style: none;
    margin: 0 0 8px;
    padding: 0;
}

.policies__row {
    align-items: center;
    border-bottom: 1px solid var(--p-content-border-color);
    display: flex;
    gap: 8px;
    padding: 6px 0;
}

.policies__row code {
    flex: 1 1 auto;
    font-family: var(--p-font-mono);
    font-size: 12px;
}

/* Stacked, so a long name and a line of JSON are not sharing 420 pixels. */
.policies__row--reading {
    align-items: stretch;
    flex-direction: column;
    gap: 2px;
}

.policies__body {
    color: var(--p-text-muted-color);
    overflow-wrap: anywhere;
}

.policies__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 0 0 8px;
}

.policies__form {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.policies__failure {
    color: var(--p-status-danger);
    font-size: 12px;
    margin: 6px 0 0;
}
</style>
